import { CHANNELS } from '@kinohelpdesk/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Globe, type LucideIcon, Smartphone, Tv } from 'lucide-react';
import { settingsApi } from '@/api/client';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';

const CHANNEL_ICONS: Record<string, LucideIcon> = {
  ios: Smartphone,
  tvos: Tv,
  service: Globe,
};

export function SettingsPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
  });

  const updateMutation = useMutation({
    mutationFn: settingsApi.update,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  });

  if (isLoading) {
    return (
      <Layout title="Настройки">
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Настройки" subtitle="Конфигурация бота и платформ">
      {/* Global Settings */}
      <div className="cyber-card p-4 mb-4">
        <h3 className="text-sm font-display text-white mb-3">Глобальные настройки</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-200">Бот включён</p>
              <p className="text-xs text-gray-500">Принимать новые заявки</p>
            </div>
            <button
              className={`cyber-button text-sm ${data?.global?.enabled ? 'cyber-button-success' : 'cyber-button-danger'}`}
              onClick={() => updateMutation.mutate({ enabled: !data?.global?.enabled })}
            >
              {data?.global?.enabled ? 'Включён' : 'Выключен'}
            </button>
          </div>

          <div>
            <label className="text-xs text-gray-500 uppercase">Лимит заявок в день</label>
            <p className="text-sm text-gray-200">{data?.global?.daily_submission_limit ?? 10}</p>
          </div>
        </div>
      </div>

      {/* Channel Settings */}
      <div className="cyber-card p-4">
        <h3 className="text-sm font-display text-white mb-3">Настройки платформ</h3>
        <div className="space-y-3">
          {Object.entries(data?.channels ?? {}).map(([channel, settings]) => {
            const channelInfo = CHANNELS[channel as keyof typeof CHANNELS];
            const ChannelIcon = CHANNEL_ICONS[channel] ?? Globe;
            return (
              <div
                key={channel}
                className="flex items-center justify-between py-2 border-b border-cyber-light last:border-0"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-cyber-gray flex items-center justify-center">
                    <ChannelIcon className="w-4 h-4 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-200">{channelInfo?.label ?? channel}</p>
                    {!settings.enabled && settings.disabled_reason && (
                      <p className="text-xs text-neon-red">{settings.disabled_reason}</p>
                    )}
                  </div>
                </div>
                <button
                  className={`cyber-button text-sm ${settings.enabled ? 'cyber-button-success' : 'cyber-button-danger'}`}
                  onClick={() => updateMutation.mutate({ channel, enabled: !settings.enabled })}
                >
                  {settings.enabled ? 'Включён' : 'Выключен'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
